import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import type { IdentityUserAri } from '@atlassian/ari/identity/user';
import type { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, {
	type HomeRecentsListQuery,
} from './recents-list/__generated__/HomeRecentsListQuery.graphql';
import type { ExtraProps, RuntimeProps, ActivityType } from './recents-list/types';

export type EntryPointParams = {
	/**
	 * Picks the type of activity results to fetch.
	 * Note that workedOn will be forced if an actor is supplied
	 */
	activityType: ActivityType;
	/**
	 * Site ARI used to unit scope results. Will be required soon
	 */
	collabContextId?: PlatformSiteAri;
	/**
	 * Will FE filter out any results that don't match these ARIs. We're treating all site ARIs
	 * (platform, townsquare and others) as the same. We only look at the resourceId
	 */
	rootContainerIds?: PlatformSiteAri[];
	/**
	 * Number of results to fetch. Defaults to 50
	 */
	first?: number;
	/**
	 * Only supply if fetching results from a user that's NOT the current user.
	 * Will fetch a list of recently worked on items from the supplied user.
	 */
	actor?: IdentityUserAri;
	/**
	 * Used for contributor filtering
	 */
	currentUser: IdentityUserAri;
};

const fetchPolicy: PreloadFetchPolicy = 'store-and-network';

export const homeRecentsListEntryPoint: EntryPoint<
	EntryPointComponent<
		{
			queryReference: HomeRecentsListQuery;
		},
		{},
		RuntimeProps,
		ExtraProps
	>,
	EntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlassian/home-recents-entrypoint" */ './recents-list/HomeRecentsListQuery'
			).then((module) => ({ default: module.HomeRecentsListQuery })), { moduleId: "68efa8af", moduleName: "HomeRecentsListQuery", modulePath: "../platform/packages/townsquare/home-recents/src/recents-list/HomeRecentsListQuery.tsx", priority: "interaction", moduleId: 'homeRecentsListEntryPoint' },
	),
	getPreloadProps: ({
		activityType,
		first,
		actor,
		collabContextId,
		rootContainerIds,
		currentUser,
	}: EntryPointParams) => ({
		queries: {
			queryReference: {
				parameters,
				variables: {
					// Other actors only support `workedOn`, so force it when an actor is provided.
					isWorkedOn: actor != null ? true : activityType === 'workedOn',
					collabContextId: collabContextId?.toString(),

					/** These IDs are a mix of platform, townsquare and other site ARIs. If you don't provide
					 * every type of ARI (even if it has the same resourceId) items can unexpectedly be filtered
					 * Explcitly setting this to undefined for now and post filtering. Hoping the unit scoping will
					 * work in the future and we won't need these.
					 */
					rootContainerIds: undefined,
					first: first ?? 50,
					hasActor: actor != null,
					actors: actor != null ? [actor.toString()] : null,
				},
				options: {
					fetchPolicy,
				},
			},
		},
		extraProps: {
			currentUser: currentUser,
			actorUser: actor,
			rootContainerIds: rootContainerIds,
		},
	}),
});
